import { useMemo } from 'react';

/**
 * Custom hook to check user permissions
 * @param {Object} user - User object from auth context
 * @returns {Object} Permission check functions
 */
export function usePermissions(user) {
  const permissions = useMemo(() => {
    // Permissions are stored directly on user object, not in a nested permissions object
    if (!user) return {};
    
    // Extract all permission fields from user
    const permissionFields = {};
    for (const key in user) {
      // Include all boolean permission fields
      if (typeof user[key] === 'boolean' && key.includes('_')) {
        permissionFields[key] = user[key];
      }
    }
    return permissionFields;
  }, [user]);

  const hasPermission = (permission) => {
    // Check directly on user object first, then in extracted permissions
    return user?.[permission] === true || permissions[permission] === true;
  };

  const hasAnyPermission = (...permissionList) => {
    return permissionList.some(perm => permissions[perm] === true);
  };

  const hasAllPermissions = (...permissionList) => {
    return permissionList.every(perm => permissions[perm] === true);
  };

  const canView = (module) => {
    const viewPermissions = {
      master_data: 'master_data_view',
      fashion_design: 'fashion_design_view',
      production_orders: 'production_orders_view',
      purchase_orders: 'purchase_orders_view',
      work_orders: 'work_orders_view',
      external_work_orders: 'external_work_orders_view',
      expenses: 'expenses_view',
      inventory: 'inventory_view',
      showroom: 'showroom_view',
      reports: 'view_reports',
      sampling_time_tracker: 'sampling_time_tracker_view',
      wastage: 'wastage_view',
      gate_pass: 'gate_pass_view'
    };
    return hasPermission(viewPermissions[module]);
  };

  const canCreate = (module) => {
    const createPermissions = {
      master_data: 'master_data_create',
      fashion_design: 'fashion_design_create',
      production_orders: 'production_orders_create',
      purchase_orders: 'purchase_orders_create',
      work_orders: 'work_orders_create',
      external_work_orders: 'external_work_orders_create',
      expenses: 'expenses_create',
      sampling_time_tracker: 'sampling_time_tracker_create',
      wastage: 'wastage_create',
      gate_pass: 'gate_pass_create'
    };
    return hasPermission(createPermissions[module]);
  };

  const canEdit = (module) => {
    const editPermissions = {
      master_data: 'master_data_edit',
      fashion_design: 'fashion_design_edit',
      production_orders: 'production_orders_edit',
      purchase_orders: 'purchase_orders_edit',
      work_orders: 'work_orders_edit',
      external_work_orders: 'external_work_orders_edit',
      expenses: 'expenses_edit',
      showroom: 'showroom_edit',
      sampling_time_tracker: 'sampling_time_tracker_edit',
      wastage: 'wastage_edit',
      gate_pass: 'gate_pass_edit'
    };
    return hasPermission(editPermissions[module]);
  };

  const canDelete = (module) => {
    const deletePermissions = {
      master_data: 'master_data_delete',
      fashion_design: 'fashion_design_delete',
      production_orders: 'production_orders_delete',
      purchase_orders: 'purchase_orders_delete',
      work_orders: 'work_orders_delete',
      external_work_orders: 'external_work_orders_delete',
      expenses: 'expenses_delete',
      sampling_time_tracker: 'sampling_time_tracker_delete',
      wastage: 'wastage_delete',
      gate_pass: 'gate_pass_delete'
    };
    return hasPermission(deletePermissions[module]);
  };

  const canApprove = (module) => {
    const approvePermissions = {
      fashion_design: 'fashion_design_approve',
      purchase_orders: 'purchase_orders_approve',
      external_work_orders: 'external_work_orders_approve',
      expenses: 'expenses_approve',
      costing: 'approve_costing',
      gate_pass: 'gate_pass_approve'
    };
    return hasPermission(approvePermissions[module]);
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canApprove
  };
}
